import type { SearchSuggestion, SearchSuggestionKind } from '@/lib/searchSuggestions'
import type { GroupedSuggestions, SuggestionItem } from '@/types/api'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { buildPropertiesSearchUrl, cityLabelFromSlug, extractIntentFromText, isAreaOnlySearch, normalizeAreaName, parseHeroSearchQuery } from '@/lib/parseSearchQuery'

const KIND_MAP: Record<string, { kind: SearchSuggestionKind; label: string }> = {
  city: { kind: 'locality', label: 'City' },
  locality: { kind: 'locality', label: 'Locality' },
  area: { kind: 'locality', label: 'Area' },
  project: { kind: 'project', label: 'Project' },
  property: { kind: 'property', label: 'Property' },
  builder: { kind: 'builder', label: 'Builder' },
  recent: { kind: 'recent', label: 'Recent' },
  trending: { kind: 'query', label: 'Trending' },
}

interface BuildOpts {
  intent: string
  city: string
  premium?: boolean
  category?: string
  /** Raw text the user typed — used to synthesize "N BHK in <place>" queries. */
  query?: string
}

function normalize(value: string) {
  return value.trim().toLowerCase()
}

/** True when a suggestion belongs to the header-selected city. */
function matchesCity(itemCity: string | undefined, selectedCity: string) {
  if (!selectedCity.trim()) return true
  if (!itemCity?.trim()) return true
  return normalize(itemCity) === normalize(selectedCity)
}

function withTab(opts: BuildOpts, params: Record<string, string | undefined>) {
  return {
    category: opts.category,
    ...(opts.premium ? { premium: 'true' } : {}),
    ...params,
  }
}

function effectiveIntent(opts: BuildOpts): string {
  if (opts.query?.trim()) {
    const extracted = extractIntentFromText(opts.query, opts.intent)
    // Only apply buy/rent/commercial when typed in the query — not from the tab.
    return extracted.explicit ? extracted.intent : ''
  }

  return opts.intent
}

function effectiveSearchTerm(opts: BuildOpts): string | undefined {
  if (!opts.query?.trim()) return undefined
  const { remainder } = extractIntentFromText(opts.query, opts.intent)
  return remainder.trim() || undefined
}

function parsedTypedQuery(opts: BuildOpts) {
  const typedRaw = opts.query?.trim()
  if (!typedRaw) return undefined
  return parseHeroSearchQuery(typedRaw, opts.city, { tabCategory: opts.category, tabIntent: opts.intent })
}

function isTypedAreaOnlyQuery(opts: BuildOpts) {
  const parsed = parsedTypedQuery(opts)
  return parsed ? isAreaOnlySearch(parsed) : false
}

/** Listing links for area-only queries use city + area only — no intent/category from tab or listing title. */
function areaOnlyPropertiesHref(city: string, area?: string) {
  return buildPropertyHref('', {
    city: city.toLowerCase(),
    area: normalizeAreaName(area) || undefined,
  })
}

function suggestionPropertiesHref(
  opts: BuildOpts,
  params: Record<string, string | undefined>,
  intent = effectiveIntent(opts),
) {
  if (isTypedAreaOnlyQuery(opts)) {
    return areaOnlyPropertiesHref(params.city ?? opts.city, params.area)
  }

  return propertiesHref(intent, withTab(opts, params))
}

function propertiesHref(intent: string, params: Record<string, string | undefined>) {
  return buildPropertyHref(
    intent === 'commercial' ? 'commercial' : intent === 'rent' ? 'rent' : intent === 'buy' ? 'buy' : '',
    {
      ...params,
      city: params.city ? params.city.toLowerCase() : undefined,
    },
  )
}

function inventorySubtitle(item: SuggestionItem, fallbackCity?: string): string | undefined {
  const locality = item.locality?.trim()
  const city = (item.city || fallbackCity || '').trim()
  if (locality && city) return `${locality}, ${city}`
  if (locality) return locality
  if (city) return city
  return propertySubtitle(item)
}

function propertySubtitle(item: SuggestionItem, fallback?: string): string | undefined {
  const parts = [item.city, item.category].filter(Boolean)
  if (item.price && item.price !== 'Call') {
    const price =
      typeof item.price === 'number'
        ? `₹${item.price.toLocaleString('en-IN')}`
        : String(item.price)
    parts.push(price)
  }
  if (parts.length > 0) return parts.join(' · ')
  return fallback
}

/**
 * Map the grouped Smart Search API response into the UI's
 * {@link SearchSuggestion} shape, in product priority order, and prepend
 * 99acres-style synthesized "N BHK …" query suggestions when relevant.
 *
 * Every href opens a filtered listing page — never a single property detail URL.
 */
export function apiSuggestionsToList(
  items: SuggestionItem[],
  opts: BuildOpts,
): SearchSuggestion[] {
  const intent = effectiveIntent(opts)
  const selectedCity = opts.city?.trim() ?? ''
  const scoped = selectedCity
    ? items.filter((item) => {
        if (item.type === 'city') return false
        return matchesCity(item.city, selectedCity)
      })
    : items

  const base = scoped.map((item, index) => {
    const map = KIND_MAP[item.type] ?? KIND_MAP.locality
    const itemCity = item.city || opts.city

    let href: string
    let subtitle: string | undefined

    switch (item.type) {
      case 'city':
        href = propertiesHref(intent, withTab(opts, { city: item.title.toLowerCase() }))
        subtitle = 'View all properties'
        break
      case 'locality':
      case 'area':
        href = suggestionPropertiesHref(opts, {
          city: itemCity.toLowerCase(),
          area:
            item.id && item.id > 0
              ? String(item.id)
              : normalizeAreaName(item.title),
        })
        subtitle = item.city
        break
      case 'property':
      case 'project': {
        const listing = listingSearchForInventory(item, { ...opts, intent })
        const areaOnly = isTypedAreaOnlyQuery(opts)
        href = suggestionPropertiesHref(opts, {
          city: itemCity.toLowerCase(),
          ...(areaOnly
            ? { area: listing.area }
            : {
                search: listing.search,
                bhk: listing.bhk,
                area: listing.area,
                category: listing.category ?? opts.category,
              }),
        })
        subtitle = inventorySubtitle(item, itemCity)
        break
      }
      case 'builder':
        href = suggestionPropertiesHref(opts, { search: item.title, city: itemCity.toLowerCase() })
        subtitle = 'Builder'
        break
      default:
        href = suggestionPropertiesHref(opts, { city: itemCity.toLowerCase(), search: item.title })
    }

    return {
      id: `${item.type}-${item.id ?? index}-${item.title}`,
      title: item.title,
      subtitle,
      kind: item.type === 'property' || item.type === 'project' ? 'project' : (map?.kind ?? 'query'),
      kindLabel:
        item.type === 'property' || item.type === 'project' ? 'Project' : (map?.label ?? 'Suggestion'),
      href,
    }
  })

  const synth = buildBhkQuerySuggestions(scoped, { ...opts, intent })
  const combined = [...synth, ...base]

  return combined.slice(0, 12)
}

/** Map recent/trending idle-panel items to listing links. */
export function idleHistoryToList(
  items: SuggestionItem[],
  opts: BuildOpts,
): SearchSuggestion[] {
  const intent = effectiveIntent(opts)

  return items.map((item, index) => {
    const itemCity = (item.city || opts.city).toLowerCase()
    const title = item.title.trim()
    const isSearchPhrase = /\b(rent|buy|sale|bhk|flat|house|shop|commercial)\b/i.test(title)

    const href = propertiesHref(
      intent,
      withTab(opts, {
        city: itemCity,
        ...(isSearchPhrase
          ? { search: title }
          : {
              area:
                item.id && item.id > 0
                  ? String(item.id)
                  : normalizeAreaName(title),
            }),
      }),
    )

    return {
      id: `${item.type}-${item.id ?? index}-${title}`,
      title,
      subtitle: item.city || opts.city,
      kind: item.type === 'recent' ? ('recent' as const) : ('query' as const),
      kindLabel: item.type === 'recent' ? 'Recent' : 'Trending',
      href,
    }
  })
}

/** Map the full grouped payload to a flat suggestion list (inventory first). */
export function groupedToList(grouped: GroupedSuggestions, opts: BuildOpts): SearchSuggestion[] {
  return apiSuggestionsToList(
    [
      ...grouped.properties,
      ...grouped.projects,
      ...grouped.localities,
      ...grouped.areas,
      ...grouped.builders,
      ...grouped.cities,
    ],
    opts,
  )
}

const BHK_RE = /(\d+)\s*bh?k?\b/i

function categorySlugFromName(name?: string): string | undefined {
  if (!name) return undefined
  const n = name.toLowerCase()
  if (n.includes('house') || n.includes('bungalow') || n.includes('tenement')) return 'house'
  if (n.includes('flat') || n.includes('apartment')) return 'flat'
  if (n.includes('villa')) return 'villa'
  if (n.includes('plot') || n.includes('land')) return 'plots'
  if (n.includes('office')) return 'office'
  if (n.includes('shop') || n.includes('showroom')) return 'shop'
  return undefined
}

/**
 * Build listing filters from a property/project suggestion so the results page
 * shows related inventory — not a single property detail page.
 */
function listingSearchForInventory(item: SuggestionItem, opts: BuildOpts) {
  const title = (item.title ?? '').trim()
  const typedRaw = (opts.query ?? '').trim()
  const typed = (effectiveSearchTerm(opts) ?? typedRaw).trim()
  const parsedTyped = typedRaw
    ? parseHeroSearchQuery(typedRaw, opts.city, { tabCategory: opts.category, tabIntent: opts.intent })
    : undefined

  const userAskedBhk = Boolean(parsedTyped?.bhk || BHK_RE.test(typedRaw))
  const isNewProjectQuery = /\bnew\s+projects?\b/i.test(typedRaw)
  // "New Project" is a property-type keyword from typed text — do not treat it
  // as permission to inherit a random project suggestion's locality.
  const userAskedCategory = Boolean(parsedTyped?.category) && !isNewProjectQuery
  const localityQuery = Boolean(parsedTyped?.area || /\b(?:near|at)\s+[a-z]/i.test(typedRaw))
  const area = normalizeAreaName(
    parsedTyped?.area || (localityQuery || userAskedCategory ? item.locality : undefined),
  )

  // Typed "new project" must stay query-led: category only, never project area/search.
  if (isNewProjectQuery) {
    return {
      search: undefined,
      bhk: userAskedBhk ? parsedTyped?.bhk : undefined,
      category: 'New Project',
      area: undefined,
    }
  }

  // Structured typed filters without an explicit locality: never inherit project area.
  if (
    typedRaw &&
    !localityQuery &&
    !parsedTyped?.area &&
    (parsedTyped?.bhk || parsedTyped?.category || parsedTyped?.saleType || parsedTyped?.verified)
  ) {
    return {
      search: parsedTyped?.search,
      bhk: parsedTyped?.bhk,
      category: parsedTyped?.category,
      area: undefined,
    }
  }

  // If user typed a generic locality phrase (e.g. "150 feet ring road touch")
  // and did not ask for specific BHK/category, keep query-led filtering.
  // Do not force area/category from an arbitrary project suggestion row.
  if (
    typedRaw &&
    !localityQuery &&
    !userAskedBhk &&
    !userAskedCategory &&
    !parsedTyped?.intentExplicit
  ) {
    return {
      search: parsedTyped?.search ?? typed,
      bhk: undefined,
      category: undefined,
      area: undefined,
    }
  }

  // User searched a place only ("aaji dam") — keep area, show all property types there.
  if (localityQuery && area && !userAskedCategory) {
    return {
      search: undefined,
      bhk: userAskedBhk ? parsedTyped?.bhk : undefined,
      category: undefined,
      area,
    }
  }

  const category =
    parsedTyped?.category ?? categorySlugFromName(item.category) ?? categorySlugFromName(title) ?? opts.category

  // User searched a place with type ("shop near mavdi") — keep area + category, not BHK from listing title.
  if (localityQuery && area) {
    return {
      search: parsedTyped?.search,
      bhk: userAskedBhk ? parsedTyped?.bhk : undefined,
      category,
      area,
    }
  }

  const bhkFromTitle = title.match(BHK_RE)
  const bhkFromTyped = typed.match(BHK_RE)

  const bhk = userAskedBhk
    ? parsedTyped?.bhk ?? (bhkFromTitle ? `${bhkFromTitle[1]} BHK` : bhkFromTyped ? `${bhkFromTyped[1]} BHK` : undefined)
    : undefined

  const tokens: string[] = []
  if (userAskedBhk && bhkFromTitle) {
    tokens.push(`${bhkFromTitle[1]} bhk`)
  }
  if (/bungalow/i.test(title)) tokens.push('bungalow')
  if (/villa/i.test(title)) tokens.push('villa')
  if (/flat|apartment/i.test(title)) tokens.push('flat')
  if (/tenement/i.test(title)) tokens.push('tenement')
  if (/shop/i.test(title)) tokens.push('shop')

  let search: string
  if (parsedTyped?.search) {
    search = parsedTyped.search
  } else if (tokens.length > 0 && userAskedBhk) {
    search = tokens.join(' ')
  } else if (typed.length >= 2) {
    search = typed
  } else {
    search = title
  }

  return { search, bhk, category, area }
}

/**
 * 99acres-style query suggestions: when the user types a BHK + place, offer
 * Buy / Resale / New / Rent variants — each links to a real filtered listing.
 */
function buildBhkQuerySuggestions(_items: SuggestionItem[], opts: BuildOpts): SearchSuggestion[] {
  if (opts.intent === 'commercial' || opts.category === 'plots') return []

  const query = (opts.query ?? '').trim()
  const m = query.match(BHK_RE)
  if (!m) return []

  const bhk = `${m[1]} BHK`
  const parsed = parseHeroSearchQuery(query, opts.city, { tabCategory: opts.category })
  const placeLabel = cityLabelFromSlug(parsed.city)

  const urlOpts = {
    defaultCity: opts.city,
    premium: opts.premium,
    tabCategory: opts.category,
  }

  const allVariants: { title: string; kindLabel: string; intent?: string; saleType?: string; forceIntent?: boolean }[] = [
    { title: `${bhk} Properties in ${placeLabel}`, kindLabel: 'All' },
    { title: `${bhk} Resale in ${placeLabel}`, kindLabel: 'Resale', intent: 'buy', saleType: 'Old Property', forceIntent: true },
    { title: `New ${bhk} in ${placeLabel}`, kindLabel: 'New Booking', intent: 'buy', saleType: 'New Property', forceIntent: true },
    { title: `${bhk} for Rent in ${placeLabel}`, kindLabel: 'Rent', intent: 'rent', forceIntent: true },
  ]

  const variants =
    opts.intent === 'rent'
      ? allVariants.filter((v) => v.intent === 'rent' || !v.intent)
      : allVariants

  return variants.map((v, i) => ({
    id: `q-${bhk}-${placeLabel}-${i}`,
    title: v.title,
    kind: 'query' as const,
    kindLabel: v.kindLabel,
    href: buildPropertiesSearchUrl(query, {
      ...urlOpts,
      intent: v.intent ?? opts.intent,
      saleType: v.saleType,
      forceIntent: v.forceIntent,
    }),
  }))
}
